import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import { endOfYear, format, startOfYear } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as DownloadIcon } from '@assets/svg/header-download-icon.svg';

import Header from '@components/common/Header/Header';
import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Select from '@components/uikit/Select/Select';
import Switch from '@components/uikit/Switch/Switch';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useActionListReportsPdfMutation } from '@pages/ActionItems/services/useActionListReportsPdfMutation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import CostSavingInfoBlock from './components/CostSavingInfoBlock/CostSavingInfoBlock';
import CostSavingsTable from './components/CostSavingsTable/CostSavingsTable';
import { useCostSavingsDetailsQuery } from './services/useCostSavingsDetailsQuery';
import { useCostSavingsLifetimeQuery } from './services/useCostSavingsLifetimeQuery';
import {
  selectCostSavingsIncludeUnverifiedSavings,
  selectCostSavingsSorting,
  selectCostSavingsYearFilter,
  useCostSavingsActions,
} from './store/useCostSavingsStore';
import style from './style.module.scss';

const CostSavingDetails = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();
  const locationSelectValue = selectAppLocationSelectValue();
  const { customerId } = useParams();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { setYearFilter, setIncludeUnverifiedSavings } = useCostSavingsActions();

  const yearFilter = selectCostSavingsYearFilter();
  const includeUnverifiedSavings = selectCostSavingsIncludeUnverifiedSavings();
  const sorting = selectCostSavingsSorting();

  const { data: profileData } = useUserProfileQuery();
  const { data: useCostSavingsDetailsData, isLoading: useCostSavingsDetailsIsLoading } =
    useCostSavingsDetailsQuery({
      locationIds:
        locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
      sorting,
      includeUnverifiedSavings,
      year: yearFilter.value,
    });
  const { data: useCostSavingsLifetimeData, isLoading: useCostSavingsLifetimeDataIsLoading } =
    useCostSavingsLifetimeQuery({
      locationIds:
        locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    });
  const { data: assetTreeData } = useAssetTreeQuery();

  const { mutate: actionListReportsPdfMutate } = useActionListReportsPdfMutation();

  const accessToAllFacilities = !!profileData?.user?.all_facilities;
  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  useEffect(() => {
    trackPageView({});
  }, []);

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  const downloadCostSavingsDetails = () => {
    const dateFormat = 'MM/dd/yyyy';
    const startDate = startOfYear(new Date(yearFilter.value, 0));
    const endDate = endOfYear(new Date(yearFilter.value, 0));
    const period = `${format(startDate, dateFormat)}-${format(endDate, dateFormat)}`;

    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    actionListReportsPdfMutate(
      {
        locationIds:
          locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue.filterIds,
        period,
        include_unverified_savings: includeUnverifiedSavings,
      },
      {
        onSuccess: (result) => {
          saveAs(
            new Blob([result?.data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            `Cost Savings Report ${format(new Date(), currentDateFormat)}.xlsx`,
          );
        },
      },
    );
  };

  return (
    <>
      <Header
        title={t('Cost Savings Details')}
        showLogo
        showFacilitySelect
        headerRightComponent={
          <DownloadIcon
            id="download-metrics"
            className={cx(style.uploadIcon, { [style.noUploadIcon]: !accessToAllFacilities })}
            onClick={downloadCostSavingsDetails}
          />
        }
        isSecondaryPage
      />
      <div className={style.costSavingsDetails}>
        <div className={style.costSavingsInfoBlock}>
          {customerInfoData?.showUnplannedDowntime && (
            <CostSavingInfoBlock
              title={t('Unplanned Downtime, Hours')}
              ytd={useCostSavingsDetailsData?.downtimeAvoided || '0.00'}
              total={
                includeUnverifiedSavings
                  ? useCostSavingsLifetimeData?.totalDowntimeAvoided
                  : useCostSavingsLifetimeData?.verifiedDowntimeAvoided
              }
              isLoading={useCostSavingsDetailsIsLoading || useCostSavingsLifetimeDataIsLoading}
            />
          )}
          <CostSavingInfoBlock
            title={`${t('Equipment/labor Savings')}, ${currentCurrency.symbol}`}
            ytd={`${currentCurrency.symbol} ${useCostSavingsDetailsData?.equipmentLaborSaving || '0.00'}`}
            total={`${currentCurrency.symbol} ${
              includeUnverifiedSavings
                ? useCostSavingsLifetimeData?.totalEquipmentLaborSaving
                : useCostSavingsLifetimeData?.verifiedEquipmentLaborSaving
            }`}
            isLoading={useCostSavingsDetailsIsLoading || useCostSavingsLifetimeDataIsLoading}
          />
          {customerInfoData?.showTotalCostSavings && (
            <CostSavingInfoBlock
              title={`${t('Total Cost Savings')}, ${currentCurrency.symbol}`}
              ytd={`${currentCurrency.symbol} ${useCostSavingsDetailsData?.costAvoided || '0.00'}`}
              total={`${currentCurrency.symbol} ${
                includeUnverifiedSavings
                  ? useCostSavingsLifetimeData?.totalSavings
                  : useCostSavingsLifetimeData?.verifiedSavings
              }`}
              isLoading={useCostSavingsDetailsIsLoading || useCostSavingsLifetimeDataIsLoading}
            />
          )}
          {customerInfoData?.showCompletionReports && (
            <CostSavingInfoBlock
              title={t('Submitted Completion Reports with metrics')}
              ytd={useCostSavingsDetailsData?.reportsCount || 0}
              total={
                includeUnverifiedSavings
                  ? useCostSavingsLifetimeData?.totalReportsCount
                  : useCostSavingsLifetimeData?.verifiedReportsCount
              }
              isLoading={useCostSavingsDetailsIsLoading || useCostSavingsLifetimeDataIsLoading}
            />
          )}
        </div>
        <div className={style.filtersBlock}>
          {useCostSavingsLifetimeDataIsLoading ? (
            <SkeletonBlock className={style.skeletonTab} />
          ) : (
            <div className={style.selectWrapper}>
              <Select
                value={yearFilter}
                onChange={setYearFilter}
                options={useCostSavingsLifetimeData?.years || []}
                itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
                type="woHelperText"
              />
            </div>
          )}
          <Switch
            value={includeUnverifiedSavings}
            onChange={() => setIncludeUnverifiedSavings(!includeUnverifiedSavings)}
            label={t('Include unverified savings from open action items')}
            type="woHelperText"
          />
        </div>
        <CostSavingsTable
          isLoading={useCostSavingsDetailsIsLoading}
          data={useCostSavingsDetailsData?.byMonth || []}
        />
      </div>
      <Tooltip
        className={style.downloadMetricsTooltip}
        anchorSelect="#download-metrics"
        content={t('Download Cost Savings Report')}
      />
    </>
  );
};

export default CostSavingDetails;
